import { useState } from 'react'

import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { EmptyState } from '@/components/ui/EmptyState'
import { ErrorState } from '@/components/ui/ErrorState'
import { LoadingState } from '@/components/ui/LoadingState'
import { PageHeader } from '@/components/ui/PageHeader'
import { CourtCard } from '@/components/venue/CourtCard'
import { CourtDeleteConfirmModal } from '@/components/venue/CourtDeleteConfirmModal'
import { CourtFormModal } from '@/components/venue/CourtFormModal'
import { useCourts } from '@/hooks/useCourts'
import type { CourtItem } from '@/lib/venue/courtTypes'
import { useVenueContext } from '@/routes/venueContext'

type ModalState =
  | { type: 'create' }
  | { type: 'edit'; court: CourtItem }
  | { type: 'delete'; court: CourtItem }
  | null

/**
 * 코트 목록·추가·수정·삭제 (요구사항 1~3, 설계 §2-1).
 *
 * 🚨 이 화면은 조립만 한다 — 저장 가능 여부(`editable`)·예약 충돌(`deletable`/`unitChangeable`) 두 종류의
 * 잠금은 모두 목록 응답이 계산해 준 필드를 그대로 쓴다(설계 D4, `courtViewModel.ts` 는 표시값만 만든다).
 */
export function VenueCourtsPage() {
  const { venueId } = useVenueContext()
  const courtsQuery = useCourts(venueId)
  const [modal, setModal] = useState<ModalState>(null)

  const view = courtsQuery.data

  return (
    <>
      <PageHeader
        title="코트 목록 관리"
        description={view ? `${view.facilityTypeLabel} · 등록된 코트 ${view.courtCount}면` : undefined}
        action={
          <Button
            disabled={!view || !view.editable}
            onClick={() => {
              setModal({ type: 'create' })
            }}
          >
            코트 추가
          </Button>
        }
      />

      {courtsQuery.isLoading ? <LoadingState rows={3} label="코트 목록을 불러오는 중" /> : null}

      {courtsQuery.isError ? (
        <ErrorState
          error={courtsQuery.error}
          onRetry={() => {
            void courtsQuery.refetch()
          }}
        />
      ) : null}

      {view ? (
        <div className="space-y-4">
          {!view.editable ? (
            <Alert tone="warning">
              <p>{view.editBlockNotice}</p>
            </Alert>
          ) : null}

          {view.courts.length === 0 ? (
            <EmptyState title="등록된 코트가 없습니다" description="코트를 추가해 예약을 받아보세요." />
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {view.courts.map((court) => (
                <CourtCard
                  key={court.courtId}
                  venueId={venueId}
                  court={court}
                  editable={view.editable}
                  onEdit={() => {
                    setModal({ type: 'edit', court })
                  }}
                  onDelete={() => {
                    setModal({ type: 'delete', court })
                  }}
                />
              ))}
            </div>
          )}
        </div>
      ) : null}

      {modal?.type === 'create' || modal?.type === 'edit' ? (
        <CourtFormModal
          venueId={venueId}
          mode={modal.type}
          court={modal.type === 'edit' ? modal.court : undefined}
          onClose={() => {
            setModal(null)
          }}
        />
      ) : null}

      {modal?.type === 'delete' ? (
        <CourtDeleteConfirmModal
          venueId={venueId}
          court={modal.court}
          onClose={() => {
            setModal(null)
          }}
        />
      ) : null}
    </>
  )
}
